import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary, formatCHF } from '../regureStyles';
import usePgmaOfferFunctions from './usePgmaOfferFunctions';
import { useTenantPgmaSpecialties } from '../../utils/productCatalog';
import PaymentFrequencyField from './PaymentFrequencyField';
import ClientSelector from './ClientSelector';
import BrokerSelect from './BrokerSelect';

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, margin: '18px 0 10px' }}>
    {children}
  </div>
);

const helpStyle = { fontSize: 11, color: S.textMuted, marginTop: 4, lineHeight: 1.35 };

const Field = ({ label, children, span = 1, help }) => (
  <div style={{ gridColumn: `span ${span}`, marginBottom: 10 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {help && <div style={helpStyle}>{help}</div>}
  </div>
);

// Segmented pill-button group. Replaces native <input type="radio"> so the active
// selection is obvious at a glance (burgundy fill + white text).
const PillGroup = ({ name, value, onChange, options }) => (
  <div role='radiogroup' aria-label={name} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0, borderRadius: 8, overflow: 'hidden', border: `1px solid ${S.borderLight}`, background: '#fff' }}>
    {options.map((opt, i) => {
      const active = String(value) === String(opt.value);
      return (
        <button
          key={opt.value}
          type='button'
          role='radio'
          aria-checked={active}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '8px 16px',
            background: active ? S.burgundy : '#fff',
            color: active ? '#fff' : S.textPrimary,
            border: 'none',
            borderLeft: i === 0 ? 'none' : `1px solid ${S.borderLight}`,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={e => { if (!active) e.currentTarget.style.background = S.bgHover; }}
          onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const PgmaOfferForm = ({ onClose, onCreated, reviseFrom = null, editDraft = null }) => {
  const { t } = useTranslation();
  const {
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProduct, paymentTerms,
    calc, calcLoading, calcError, ageNotEligible,
    salaryCapped, insuredSalary, enteredSalary,
    saving, saveError,
    saveDraft, createOffer,
  } = usePgmaOfferFunctions({ onClose, onCreated, reviseFrom, editDraft });
  // Field-of-activity options published by the tenant. Empty list = the
  // field stays the free-text input it has always been, so a tenant that
  // hasn't configured a vocabulary is unaffected.
  const specialties = useTenantPgmaSpecialties();

  const coverageType = productConfig.coverageType;
  const showAccidentBreakdown = coverageType === 'maladie_accident';
  // The affiliation names ONE preneur and accepts either shape: a practitioner
  // in their own name, or a practice. So Create is gated on being able to name
  // SOMEBODY -- not on Nom/Prenom, which a practice-held affiliation has no
  // reason to carry. Mirrors canNamePolicyholder() on the server; the entity
  // types listed there ("Raison individuelle", "Sarl" alone, ...) are rejected
  // server-side, so a legal form typed into Raison sociale is refused there
  // rather than silently accepted here.
  const policyholderNameable = Boolean(
    (String(policyholder.lastName || '').trim() && String(policyholder.firstName || '').trim())
    || String(policyholder.companyName || '').trim(),
  );

  return (
    <>
      <SectionTitle>{t('offers.common.policyholderData')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('offers.common.client', { defaultValue: 'Client' })} span={2}>
          <ClientSelector value={clientId} onSelect={selectClient} onClear={clearClient} />
        </Field>
        <Field label={t('offers.common.broker', { defaultValue: 'Broker' })} span={2}>
          <BrokerSelect value={brokerId} onChange={setBrokerId} inputStyle={inputStyle} />
        </Field>
        {/* "Raison sociale" alone was read as "what KIND of business are you":
            practitioners answered it with their legal form ("Raison
            individuelle", "Indépendant") and left Nom/Prénom blank, so the
            affiliation had no name to print. Seen on several ARGOS offers. The
            label now names what goes in it and the helper says it may be left
            empty, which is the honest answer for a doctor trading under their
            own name. PGMA-specific keys on purpose: PGM insures a company's
            employees, where "leave blank" would be wrong guidance. */}
        <Field
          label={t('offers.pgma.companyNameLabel')}
          help={t('offers.pgma.companyNameHelp')}
          span={2}
        >
          <input value={policyholder.companyName} onChange={e => updatePolicyholder('companyName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.address')} span={2}>
          <textarea
            value={policyholder.address}
            onChange={e => updatePolicyholder('address', e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field label={t('offers.common.phone')}>
          <input value={policyholder.phone} onChange={e => updatePolicyholder('phone', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.email')}>
          <input type='email' value={policyholder.email} onChange={e => updatePolicyholder('email', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.pgma.businessBranch')} span={2}>
          {specialties.length ? (
            <select
              value={policyholder.branch}
              onChange={e => updatePolicyholder('branch', e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value=''>{t('offers.pgma.branchSelect')}</option>
              {specialties.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : (
            <input value={policyholder.branch} onChange={e => updatePolicyholder('branch', e.target.value)} style={inputStyle} placeholder={t('offers.pgma.branchPlaceholder')} />
          )}
        </Field>
        <Field label={t('offers.pgma.lastName')}>
          <input value={policyholder.lastName} onChange={e => updatePolicyholder('lastName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.pgma.firstName')}>
          <input value={policyholder.firstName} onChange={e => updatePolicyholder('firstName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.pgma.dateOfBirth')}>
          <input type='date' value={policyholder.dateOfBirth} onChange={e => updatePolicyholder('dateOfBirth', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.pgma.gender')}>
          <select value={policyholder.gender} onChange={e => updatePolicyholder('gender', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value=''>—</option>
            <option value='M'>{t('offers.pgma.genderMale')}</option>
            <option value='F'>{t('offers.pgma.genderFemale')}</option>
          </select>
        </Field>
      </div>

      <SectionTitle>{t('offers.pgma.productConfig')}</SectionTitle>
      <Field label={t('offers.pgma.annualSalary')} span={2}>
        <input
          type='number'
          value={productConfig.annualSalary}
          onChange={e => updateProduct('annualSalary', e.target.value)}
          style={inputStyle}
          min={0}
        />
        {salaryCapped && (
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>
            {t('offers.pgma.salaryCapNotice', {
              entered: formatCHF(enteredSalary),
              insured: formatCHF(insuredSalary),
            })}
          </div>
        )}
      </Field>
      <Field label={t('offers.common.coverageType')} span={2}>
        <PillGroup
          name='coverageType'
          value={coverageType}
          onChange={(v) => updateProduct('coverageType', v)}
          options={[
            { value: 'maladie_only',     label: t('offers.pgma.coverageSickness') },
            { value: 'maladie_accident', label: t('offers.pgma.coverageBoth') },
          ]}
        />
      </Field>
      <Field label={t('offers.common.waitingPeriod')} span={2}>
        <PillGroup
          name='waitingPeriod'
          value={String(productConfig.waitingPeriod)}
          onChange={(v) => updateProduct('waitingPeriod', v)}
          options={[30, 60, 90].map(d => ({ value: String(d), label: `${d} ${t('offers.common.daysUnit')}` }))}
        />
      </Field>
      <Field label={t('offers.common.effectiveDate')} span={2}>
        <input type='date' value={productConfig.effectiveDate} onChange={e => updateProduct('effectiveDate', e.target.value)} style={inputStyle} />
      </Field>
      {/* NO correspondence-recipient selector here, deliberately. The AROMED
          PGMA template has no correspondence-address row — the renderer calls
          resolveCorrespondenceBlock only in buildRcProOfferDocument — so a
          selector would ask the operator a question the document never answers.
          Adding the row would mean inventing wording AROMED did not ask for.

          The FIELD still exists end to end: pgmaController allow-lists
          correspondenceRecipient and it defaults to 'insured', ready for
          document-delivery routing. Only the control is absent.
          See tests/correspondenceRecipientRoundTrip.test.mjs. */}
      {/* Payment frequency — the shared control, driven only by the tenant's
          PGMA paymentTerms. PGMA used to offer every frequency, monthly
          included, with no surcharge, and the server discarded the choice. */}
      <Field label={t('offers.pgm.paymentFrequency')} span={2}>
        <PaymentFrequencyField
          terms={paymentTerms}
          premium={calc?.totalPremium}
          value={productConfig.paymentFrequency || paymentTerms.frequency}
          onChange={(v) => updateProduct('paymentFrequency', v)}
        />
      </Field>

      <SectionTitle>{t('offers.pgma.premiumCalc')}</SectionTitle>
      {ageNotEligible && (
        <div style={{ padding: '12px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 10 }}>
          <strong>{t('offers.pgma.ageNotEligible', { age: ageNotEligible.age })}</strong> {ageNotEligible.message}
        </div>
      )}
      {calcError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 10 }}>
          {calcError}
        </div>
      )}
      {calcLoading && !calc && (
        <div style={{ padding: 14, color: S.textMuted, fontSize: 12 }}>{t('offers.common.calculating')}</div>
      )}
      {calc && (
        <div style={{ background: 'rgba(128,0,32,0.03)', border: `1px solid ${S.burgundy}30`, borderRadius: 10, padding: 14 }}>
          {salaryCapped && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 6 }}>
              <span>{t('offers.pgma.premiumBasis')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.inputs.effectiveSalary)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 6 }}>
            <span>{t('offers.pgma.sicknessRate')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{(calc.rates.maladieRate * 100).toFixed(3)}%</span>
          </div>
          {showAccidentBreakdown && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 6 }}>
              <span>{t('offers.pgma.combinedRate')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{(calc.rates.maladieAccidentRate * 100).toFixed(3)}%</span>
            </div>
          )}
          <div style={{ borderTop: `1px dashed ${S.borderLight}`, margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: S.textPrimary, marginBottom: 4 }}>
            <span>{t('offers.pgma.sicknessPremium')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.breakdown.maladiePremium)}</span>
          </div>
          {showAccidentBreakdown && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: S.textPrimary, marginBottom: 4 }}>
              <span>{t('offers.pgma.accidentPremium')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.breakdown.accidentPremium)}</span>
            </div>
          )}
          <div style={{ borderTop: `1px solid ${S.borderLight}`, margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('offers.common.totalPremium')}</span>
            <span style={{ fontSize: 24, fontWeight: 800, color: S.burgundy, fontVariantNumeric: 'tabular-nums' }}>{formatCHF(calc.totalPremium)}</span>
          </div>
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8 }}>
            {/* The insured amount is stated plainly. The "{{n}}% of salary" clause
                appears only when a coverage factor below 100% is actually
                configured -- printing "100% of insured salary" beside the same
                number the row above already shows is noise, and the 80% this
                replaces was simply untrue for PGMA. */}
            {Number(calc.inputs.coveragePercent) < 1
              ? t('offers.pgma.coverageOfSalary', { percent: Math.round(calc.inputs.coveragePercent * 100), amount: formatCHF(calc.inputs.insuredAmount) })
              : t('offers.pgma.insuredAmountLine', { amount: formatCHF(calc.inputs.insuredAmount) })}{' '}
            {t('offers.pgma.maxBenefit', { days: calc.maxBenefitDays })}{calc.avsAgeReached ? ' ' + t('offers.pgma.avsCapApplied') : ''}.
          </div>
          {calc.minimumPremiumApplied && (
            <div style={{ fontSize: 11, color: S.warning, marginTop: 6 }}>
              {t('offers.pgma.minPremiumApplied', { value: formatCHF(calc.minimumPremium) })}
            </div>
          )}
        </div>
      )}

      <SectionTitle>{t('offers.common.healthDeclaration')}</SectionTitle>
      <div style={{ padding: '12px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 14 }}>
        {t('offers.common.healthDeclarationRequired')}
      </div>

      {saveError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 10 }}>
          {saveError}
        </div>
      )}

      {/* The affiliation names the insured practitioner as the policyholder, so
          the offer cannot be created without one. Practitioners were entering
          their entity type ("Raison individuelle") in Raison sociale and leaving
          these blank, and the document had nothing to print. Drafts stay
          saveable half-filled -- only Create is gated. */}
      {!policyholderNameable && (
        <div style={{ padding: '10px 14px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, fontSize: 12, color: '#854d0e', marginBottom: 10 }}>
          {t('offers.pgma.insuredNameRequired')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
          {t('offers.common.cancel')}
        </BtnSecondary>
        <BtnSecondary onClick={saveDraft} disabled={!calc || saving || !!ageNotEligible} style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving || !!ageNotEligible) ? 0.6 : 1 }}>
          {saving ? '…' : t('offers.common.saveDraft')}
        </BtnSecondary>
        <BtnPrimary onClick={createOffer} disabled={!calc || saving || !!ageNotEligible || !policyholderNameable} style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving || !!ageNotEligible || !policyholderNameable) ? 0.6 : 1 }}>
          <Icon name='check' color='#fff' size={15} /> {saving ? t('offers.common.creating') : t('offers.common.createOffer')}
        </BtnPrimary>
      </div>
    </>
  );
};

export default PgmaOfferForm;
